/**
 * Announcement Slider Web Component
 * Lightweight carousel for rotating announcement bar content
 * Supports auto-play, looping, keyboard navigation, and touch gestures
 */

class AnnouncementSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayInterval = null;
    this.isTransitioning = false;
    this.abortController = new AbortController();
    this.touchStartX = 0;
    this.touchEndX = 0;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.slides = this.querySelectorAll('[data-slide]');
    this.prevBtn = this.querySelector('[data-prev-btn]');
    this.nextBtn = this.querySelector('[data-next-btn]');
    this.sliderContainer = this.querySelector('[data-slider-container]');

    if (this.slides.length === 0) return;

    // Only initialize slider for multiple slides
    if (this.slides.length > 1) {
      this.setupEventListeners();
      this.startAutoplay();
    }

    // Always show first slide
    this.goToSlide(0);
  }

  setupEventListeners() {
    const signal = this.abortController.signal;

    // Navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev(), { signal });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next(), { signal });
    }

    // Keyboard navigation
    this.addEventListener('keydown', (e) => this.handleKeyboard(e), { signal });

    // Touch/swipe support
    this.addEventListener('touchstart', (e) => this.handleTouchStart(e), { signal });
    this.addEventListener('touchend', (e) => this.handleTouchEnd(e), { signal });

    // Pause autoplay on hover
    if (this.autoplayInterval) {
      this.addEventListener('mouseenter', () => this.stopAutoplay(), { signal });
      this.addEventListener('mouseleave', () => this.startAutoplay(), { signal });
    }
  }

  startAutoplay() {
    const delay = parseInt(this.getAttribute('data-autoplay-delay'), 10) || 5000;
    const autoplay = this.getAttribute('data-autoplay') !== 'false';

    if (!autoplay || this.slides.length <= 1) return;

    if (this.autoplayInterval) clearInterval(this.autoplayInterval);

    this.autoplayInterval = setInterval(() => {
      this.next();
    }, delay);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  goToSlide(index) {
    if (this.isTransitioning || this.slides.length === 0) return;

    this.isTransitioning = true;

    // Handle looping
    const maxIndex = this.slides.length - 1;
    if (index < 0) {
      this.currentIndex = maxIndex;
    } else if (index > maxIndex) {
      this.currentIndex = 0;
    } else {
      this.currentIndex = index;
    }

    // Update slide visibility
    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i !== this.currentIndex ? 'true' : 'false');
      slide.classList.toggle('is-active', i === this.currentIndex);
    });

    // Update button states
    this.updateButtonStates();

    // Emit custom event for external listeners
    this.dispatchEvent(
      new CustomEvent('slide-change', {
        detail: { currentIndex: this.currentIndex, totalSlides: this.slides.length },
        bubbles: true,
      })
    );

    // Re-enable transitions after CSS animation completes
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.isTransitioning = false;
      }, 300); // Match CSS transition duration
    });
  }

  next() {
    this.goToSlide(this.currentIndex + 1);
  }

  prev() {
    this.goToSlide(this.currentIndex - 1);
  }

  handleKeyboard(e) {
    if (this.slides.length <= 1) return;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.next();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prev();
    }
  }

  handleTouchStart(e) {
    this.touchStartX = e.changedTouches[0].screenX;
  }

  handleTouchEnd(e) {
    this.touchEndX = e.changedTouches[0].screenX;
    this.handleSwipe();
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swiped left, go to next
        this.next();
      } else {
        // Swiped right, go to previous
        this.prev();
      }
    }
  }

  updateButtonStates() {
    if (!this.prevBtn || !this.nextBtn) return;

    const isAtStart = this.currentIndex === 0;
    const isAtEnd = this.currentIndex === this.slides.length - 1;

    // Update aria-label to announce current position
    const positionText = `${this.currentIndex + 1} of ${this.slides.length}`;
    this.prevBtn.setAttribute('aria-label', `Previous announcement, ${positionText}`);
    this.nextBtn.setAttribute('aria-label', `Next announcement, ${positionText}`);
  }

  cleanup() {
    this.stopAutoplay();
    this.abortController.abort();
  }
}

customElements.define('announcement-slider', AnnouncementSlider);
